
import { AsyncPipe, NgClass } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import {
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormControl,
    UntypedFormGroup
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { IconComponent } from "@components/icon/icon.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SearchComponent } from "@components/search/search.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import _ from "lodash";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { InputProcessorType } from "~app/common/components/input/input-processor";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { AnsvarigAnvandare } from "~app/features/virkeskop/models/avtal-models";
import { AtgardProduktionsdatum } from "~app/features/virkeskop/models/leveransvirkesplan-models";
import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { canEdit } from "~app/role-permissions";
import { DisabledControlDirective } from "../../../../common/directives/disabled-control/disabled-control.directive";
import { BYL, Malklass } from "../../../../common/models/constants";
import { ProduktionsstatusPipe } from "../../../../common/pipes/produktionsstatus.pipe";
import { Geometri } from "../../models/karta-models";
import { VirkeskopKontraktstyp } from "../../models/kontrakt-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { AvverkningsanmalanMediatorService } from "../../services/avverkningsanmalan-mediator.service";
import { TradslagMediatorService } from "../../services/tradslag-mediator.service";
import { VirkeskostnaderUtbyteService } from "../../services/virkeskostnader-utbyte.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VirkeskopValidationRuleDirective } from "../../validation/directives/virkeskop-validation-rule.directive";
@Component({
    selector: "rask-atgard-detaljer",
    templateUrl: "./atgard-detaljer.component.html",
    styleUrls: ["./atgard-detaljer.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        VirkeskopValidationRuleDirective,
        DisabledControlDirective,
        DatepickerComponent,
        TextmultilineComponent,
        NgClass,
        ButtonComponent,
        InputNumberComponent,
        IconComponent,
        SearchComponent,
        SimpleSelectorComponent,
        RadiobuttonGroupComponent,
        TextareaComponent,
        AsyncPipe,
        ProduktionsstatusPipe
    ]
})
export class AtgardDetaljerComponent implements OnInit, OnDestroy {

    get atgard(): Atgard {
        return this._atgard;
    }

    get kontrakt$() {
        return this.kontraktMediatorService.kontrakt$;
    }

    protected externBeteckningInfotext = "Referens hos kund/leverantör";
    protected utbyteIconWarningText: string;
    protected showUtbyteIconWarning = false;
    protected malKlasser = Malklass.allaValbara;

    protected numberDecimalProcessor = InputProcessorType.numberDecimalProcessor(2, true);

    protected _atgard: Atgard;
    protected form: FormGroup = new UntypedFormGroup({
        beteckning: new UntypedFormControl(),
        externBeteckning: new UntypedFormControl(),
        nettoareal: new UntypedFormControl(),
        tillganglighetVag: new UntypedFormControl(),
        tillganglighetSkog: new UntypedFormControl(),
        skotningsavstand: new UntypedFormControl(),
        barighet: new UntypedFormControl(),
        ytstruktur: new UntypedFormControl(),
        lutning: new UntypedFormControl(),
        medelstam: new UntypedFormControl(),
        antalFS: new UntypedFormControl(),
        grotuttagForeslaget: new UntypedFormControl(),
        uttagsanvisning: new UntypedFormControl(),
        atgardsnamn: new UntypedFormControl(),
        foreslagetProduktionsdatumFrom: new UntypedFormControl(),
        foreslagetProduktionsdatumTom: new UntypedFormControl(),
        planerare: new UntypedFormGroup({
            userName: new UntypedFormControl()
        }),
        datumSkordat: new UntypedFormControl(),
        grundytaForeAvv: new UntypedFormControl(),
        grundytaEfterAvv: new UntypedFormControl(),
        malklass: new UntypedFormControl()
    });

    protected barighetItems = BYL.barighetItems;
    protected ytstrukturItems = BYL.ytstrukturItems;
    protected lutningItems = BYL.lutningItems;

    protected grotUttagRadioButtons: IRadiobuttonItem[] = [
        {
            key: true,
            value: "Ja"
        },
        {
            key: false,
            value: "Nej"
        }
    ];

    protected isGrot: boolean;
    protected hasKoppladRundvirke: boolean;
    private readonly ngUnsubscribe$ = new Subject<void>();

    private pristine = false;

    private readonly avverkningsanmalanMediatorService = inject(AvverkningsanmalanMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly tradslagMediatorService = inject(TradslagMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kartaMediatorService = inject(VkKartaMediatorService);
    private readonly avtalService = inject(AvtalService);

    private readonly canEdit = canEdit();

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(atgard => {
                this._atgard = atgard;
                this.atgardChanged();
                this.handleUtbyteIconWarning();
            });

        this.atgardMediatorService.atgardUpdate$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.handleUtbyteIconWarning());

        this.kartaMediatorService.atgardGeometriChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.updateFromChangedGeometri(geometri));

        this.kartaMediatorService.atgardGeometriDeleted$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(geometri => this.updateFromChangedGeometri(geometri));

        this.form.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.onFormValueChanges());

        this.atgardMediatorService.calculatedMedelstam$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(value => {
                const control = this.form.controls.medelstam;
                control.setValue(value);
                control.markAsDirty();
                this.atgard.medelstam = value;
            });

        this.tradslagMediatorService.calculatedGrundytaFore$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(value => {
                const control = this.form.controls.grundytaForeAvv;
                control.setValue(value);
                control.markAsDirty();
                this.form.updateValueAndValidity();
            });

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(value => {
            if (value) {
                requestAnimationFrame(() => {
                    this.form.disable();
                });
            }
        });

        this.kontraktMediatorService.kontrakt$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(kontrakt => {
                this.isGrot = kontrakt.isGrot;
            });

        this.kontraktMediatorService.kontraktKopplingar$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(kopplingar => {
                const matchingKedjaId = kopplingar?.kopplingar
                    ?.find(k => k.koppladVKAtgardBeteckning === this.atgard.beteckning)?.kedjaId;

                this.hasKoppladRundvirke = matchingKedjaId != null &&
                    kopplingar?.kopplingar?.some(k =>
                        k.kedjaId === matchingKedjaId && k.koppladVKKontraktTyp === VirkeskopKontraktstyp.Rundvirke);
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    protected atgardForeslagetProduktionsdatumChanged(): void {
        this.kontraktMediatorService.atgardForeslagetProduktionsdatumchanged$.next(this.getatgardForeslagetProduktionsdatum());
    }

    protected getatgardForeslagetProduktionsdatum(): AtgardProduktionsdatum {
        const dto = new AtgardProduktionsdatum();
        const period = this.atgard.foreslagetProduktionsdatum;

        dto.periodFrom = period.periodFrom;
        dto.periodTom = period.periodTom;
        dto.atgardId = this.atgard.id;
        dto.guid = this.atgard.guid;

        return dto;
    }

    protected ansvarigPlanerareCompareWith(item: AnsvarigAnvandare, selected: AnsvarigAnvandare) {
        return item.userName === selected.userName;
    }
    protected searchForAnsvarigPlanerareTypeAhead = (input: string) => this.avtalService.getFaltplanerareTypeAhead(input)
        .pipe(map(typeahead =>
            typeahead.map(x => ({
                userName: x.kod,
                name: x.namn
            }))
        ));

    protected onRemoveAtgard(): void {
        this.atgardMediatorService.removeAtgard();
    }

    protected searchForAnsvarigVirkeskopareTypeAhead = (input: string) => this.avtalService.getFaltplanerareTypeAhead(input);

    protected nettoarealChanged() {
        this.kontraktMediatorService.updateNettoarealSrc.next({
            atgardGuid: this.atgard.guid,
            nettoareal: this.atgard.nettoareal,
            atgardstyp: this.atgard.atgardstyp,
            avverkningsanmalanGodkantDatum: this.avverkningsanmalanMediatorService.avverkningsanmalan?.godkantDatum,
            beteckning: this.atgard.beteckning
        });
    }

    protected disableMalklass(): boolean {
        return this.atgard.isReadonly || (this.atgard.nvbHasMalklass && !ObjectUtils.isNullOrUndefined(this.atgard.malklass));
    }

    private onFormValueChanges() {
        // Checks if the form was modified by atgardChanged
        if (this.pristine || this.form.pristine) {
            return;
        }

        Object.keys(this.form.controls).forEach(key => {
            const control = this.form.get(key);
            if (control.dirty) {
                this.atgard[key] = control.value;
            }
        });

        this.atgardMediatorService.update(this.atgard);
    }

    private atgardChanged() {
        this.pristine = true;
        this.form.patchValue({
            beteckning: this.atgard.beteckning,
            externBeteckning: this.atgard.externBeteckning,
            nettoareal: this.atgard.nettoareal,
            tillganglighetVag: this.atgard.tillganglighetVag,
            tillganglighetSkog: this.atgard.tillganglighetSkog,
            skotningsavstand: this.atgard.skotningsavstand,
            barighet: this.atgard.barighet,
            ytstruktur: this.atgard.ytstruktur,
            lutning: this.atgard.lutning,
            medelstam: this.atgard.medelstam,
            antalFS: this.atgard.antalFS,
            grotuttagForeslaget: this.atgard.grotuttagForeslaget,
            uttagsanvisning: this.atgard.uttagsanvisning,
            atgardsnamn: this.atgard.atgardsnamn,
            foreslagetProduktionsdatumFrom: this.atgard.foreslagetProduktionsdatumFrom,
            foreslagetProduktionsdatumTom: this.atgard.foreslagetProduktionsdatumTom,
            planerare: {
                userName: this.atgard.planerare?.userName ?? null
            },
            datumSkordat: this.atgard.datumSkordat,
            grundytaForeAvv: this.atgard.grundytaForeAvv,
            grundytaEfterAvv: this.atgard.grundytaEfterAvv,
            malklass: this.atgard.malklass
        });
        this.pristine = false;
        this.nettoarealChanged();
    }

    private updateFromChangedGeometri(geometri: Geometri): void {
        if (geometri?.guid === this._atgard.guid) {
            this.atgard.nettoareal = geometri.areal;
            this.atgard.geometri = geometri.mapGeometry.wkt;
            if (ObjectUtils.hasValue(geometri.kartgeometri?.anteckning)) {
                this.atgard.uttagsanvisning = geometri.kartgeometri.anteckning;
            }

            this.atgard.barighet = geometri?.kartgeometri?.barighet ?? this.atgard.barighet;
            this.atgard.ytstruktur = geometri?.kartgeometri?.ytstruktur ?? this.atgard.ytstruktur;
            this.atgard.lutning = geometri?.kartgeometri?.lutning ?? this.atgard.lutning;

            this.atgardChanged();
        }
    }

    private handleUtbyteIconWarning(): void {
        this.showUtbyteIconWarning = false;

        if (!_.isNil(this.atgard.nettoareal) && !_.isNil(this.atgard.utbyteInlastAreal) &&
            VirkeskostnaderUtbyteService.arealDiffers(this.atgard.nettoareal, this.atgard.utbyteInlastAreal)) {
            this.utbyteIconWarningText = VirkeskostnaderUtbyteService.arealDiffMessage(this.atgard.utbyteInlastFran);
            this.showUtbyteIconWarning = true;
        }
    }
}
